<template>
  <div class="boards-page">
    <div class="boards-inner">

      <!-- Header -->
      <div class="page-header">
        <div>
          <h1>My Wishboards</h1>
          <p class="page-sub">Signed in as {{ userEmail }}</p>
        </div>
        <router-link to="/WishboardCreate">
          <button class="btn-new">
            <i class="bx bx-plus"></i> New Board
          </button>
        </router-link>
      </div>

      <!-- Controls: filter + sort + layout toggle -->
      <div class="controls" v-if="!loading && !fetchError && wishboards.length > 0">
        <div class="controls-left">
          <button
            v-for="f in filters"
            :key="f.key"
            class="filter-pill"
            :class="{ active: activeFilter === f.key }"
            @click="activeFilter = f.key"
          >{{ f.label }}</button>
        </div>
        <div class="controls-right">
          <select class="sort-select" v-model="sortBy">
            <option value="date">Newest first</option>
            <option value="wishes">Most wishes</option>
            <option value="alpha">A → Z</option>
          </select>
          <button class="layout-btn" :class="{ active: layout === 'grid' }" @click="layout = 'grid'" title="Grid view">
            <i class="bx bx-grid-alt"></i>
          </button>
          <button class="layout-btn" :class="{ active: layout === 'list' }" @click="layout = 'list'" title="List view">
            <i class="bx bx-list-ul"></i>
          </button>
        </div>
      </div>
      <div v-if="loading" class="state-center">
        <i class="bx bx-loader-alt bx-spin state-icon"></i>
        <p>Loading your boards…</p>
      </div>

      <!-- Error -->
      <div v-else-if="fetchError" class="state-center">
        <i class="bx bx-error-circle state-icon warn"></i>
        <p>{{ fetchError }}</p>
        <button class="btn-ghost" @click="fetch">Try again</button>
      </div>

      <!-- Empty -->
      <div v-else-if="wishboards.length === 0" class="state-center">
        <i class="bx bx-gift state-icon"></i>
        <p>You haven't created a Wishboard yet.</p>
        <router-link to="/WishboardCreate">
          <button class="btn-new">Create your first Wishboard</button>
        </router-link>
      </div>

      <template v-else>

        <!-- GRID MODE -->
        <template v-if="layout === 'grid'">
          <!-- Hero: first board -->
          <div
            class="hero-card"
            :style="displayBoards[0].wishboard_background_url
              ? { backgroundImage: displayBoards[0].wishboard_background_url }
              : {}"
            @click="openBoard(displayBoards[0].id)"
          >
            <div class="hero-card-overlay">
              <div class="hero-card-inner">
                <div class="hero-meta">
                  <span class="hero-badge">{{ sortBy === 'date' ? 'Latest' : sortBy === 'wishes' ? 'Most active' : 'First' }}</span>
                  <span class="hero-date">{{ parse_date_fn(displayBoards[0]['Create Date']) }}</span>
                </div>
                <h2 class="hero-title">{{ displayBoards[0].Title }}</h2>
                <p class="hero-sub">For {{ displayBoards[0].Recipient }}</p>
                <div class="hero-footer">
                  <div class="hero-wishes">
                    <i class="bx bx-message-rounded-dots"></i>
                    {{ displayBoards[0].wish_count }}
                    <span v-if="displayBoards[0].Purchased === 'Free'">/ {{ displayBoards[0].max_wish_count }}</span>
                    wishes
                  </div>
                  <button class="hero-btn" @click.stop="openBoard(displayBoards[0].id)">
                    Open Board <i class="bx bx-right-arrow-alt"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Rest: 2-col card grid -->
          <div v-if="displayBoards.length > 1" class="rest-section">
            <div class="rest-heading">{{ displayBoards.length - 1 }} more board{{ displayBoards.length - 1 !== 1 ? 's' : '' }}</div>
            <div class="rest-grid">
              <div
                v-for="wb in displayBoards.slice(1)"
                :key="wb.id"
                class="rest-card"
                :style="wb.wishboard_background_url
                  ? { backgroundImage: wb.wishboard_background_url }
                  : {}"
                @click="openBoard(wb.id)"
              >
                <div class="rest-card-overlay">
                  <div class="rest-card-inner">
                    <div class="rest-card-top">
                      <span v-if="wb.Purchased !== 'Free'" class="badge-premium">Premium</span>
                      <span class="rest-card-date">{{ parse_date_fn(wb['Create Date']) }}</span>
                    </div>
                    <div class="rest-card-body">
                      <div class="rest-card-title">{{ wb.Title }}</div>
                      <div class="rest-card-for">For {{ wb.Recipient }}</div>
                    </div>
                    <div class="rest-card-footer">
                      <div class="rest-card-wishes">
                        <i class="bx bx-message-rounded-dots"></i>
                        {{ wb.wish_count }}
                        <span v-if="wb.Purchased === 'Free'">/ {{ wb.max_wish_count }}</span>
                      </div>
                      <div class="rest-card-actions" @click.stop>
                        <button v-if="wb.Purchased === 'Free' && parseInt(wb.wish_count) >= parseInt(wb.max_wish_count) * 0.8" class="btn-upgrade" @click="navigateToPaymentPage(wb.id)">Upgrade</button>
                        <button class="rest-open-btn" @click="openBoard(wb.id)">Open <i class="bx bx-right-arrow-alt"></i></button>
                      </div>
                    </div>
                    <div class="rest-progress" v-if="wb.Purchased === 'Free'">
                      <div class="rest-progress-bar">
                        <div class="rest-progress-fill" :class="{ warn: parseInt(wb.wish_count) / parseInt(wb.max_wish_count) > 0.8 }" :style="{ width: Math.min(100, (parseInt(wb.wish_count) / parseInt(wb.max_wish_count)) * 100) + '%' }"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- LIST MODE -->
        <div v-else class="list-view">
          <div
            v-for="wb in displayBoards"
            :key="wb.id"
            class="list-item"
            @click="openBoard(wb.id)"
          >
            <div class="list-thumb" :style="wb.wishboard_background_url ? { backgroundImage: wb.wishboard_background_url } : {}">
              <i v-if="!wb.wishboard_background_url" class="bx bx-gift list-thumb-icon"></i>
            </div>
            <div class="list-info">
              <div class="list-title">{{ wb.Title }}</div>
              <div class="list-meta">
                For {{ wb.Recipient }}
                <span class="dot">·</span>
                {{ parse_date_fn(wb['Create Date']) }}
                <span class="dot">·</span>
                {{ wb.wish_count }}<span v-if="wb.Purchased === 'Free'"> / {{ wb.max_wish_count }}</span> wishes
                <span v-if="wb.Purchased !== 'Free'" class="badge-premium-inline">Premium</span>
              </div>
              <div class="list-progress" v-if="wb.Purchased === 'Free'">
                <div class="list-progress-bar">
                  <div class="list-progress-fill" :class="{ warn: parseInt(wb.wish_count) / parseInt(wb.max_wish_count) > 0.8 }" :style="{ width: Math.min(100, (parseInt(wb.wish_count) / parseInt(wb.max_wish_count)) * 100) + '%' }"></div>
                </div>
              </div>
            </div>
            <div class="list-actions" @click.stop>
              <button v-if="wb.Purchased === 'Free' && parseInt(wb.wish_count) >= parseInt(wb.max_wish_count) * 0.8" class="btn-upgrade" @click="navigateToPaymentPage(wb.id)">Upgrade</button>
              <button class="list-open-btn" @click="openBoard(wb.id)"><i class="bx bx-right-arrow-alt"></i></button>
            </div>
          </div>
        </div>

      </template>
    </div>
  </div>
</template>

<script>
import axios from '../axiosConfig'

export default {
  name: 'WishboardList',
  data() {
    return {
      wishboards: [],
      loading: true,
      fetchError: null,
      sortBy: 'date',
      activeFilter: 'all',
      layout: localStorage.getItem('wb_layout') || 'grid',
      filters: [
        { key: 'all', label: 'All' },
        { key: 'paid', label: 'Premium' },
      ],
    }
  },
  computed: {
    userEmail() {
      return this.$store.getters.getUser || ''
    },
    displayBoards() {
      let boards = [...this.wishboards]

      // Filter
      if (this.activeFilter === 'paid') boards = boards.filter(wb => wb.Purchased !== 'Free')

      // Sort
      if (this.sortBy === 'date') {
        boards.sort((a, b) => new Date(b['Create Date']) - new Date(a['Create Date']))
      } else if (this.sortBy === 'wishes') {
        boards.sort((a, b) => parseInt(b.wish_count) - parseInt(a.wish_count))
      } else if (this.sortBy === 'alpha') {
        boards.sort((a, b) => a.Title.localeCompare(b.Title))
      }

      return boards
    },
  },
  watch: {
    layout(val) {
      localStorage.setItem('wb_layout', val)
    }
  },
  methods: {
    parse_date_fn(dt) {
      return new Date(dt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    openBoard(id) {
      this.$router.push({ name: 'Wishboard', query: { wishboard_id: id } })
    },
    navigateToPaymentPage(id) {
      this.$router.push({ name: 'Payment', query: { wishboard_id: id } })
    },
    fetch() {
      this.loading = true
      this.fetchError = null

      const token = this.$store.getters.getGToken
      const user  = this.$store.getters.getUser

      // Same race as WishboardCreate.validate_tok: this runs on mounted(), which
      // can fire before the credential stored by Login.onSignIn has been read from
      // the store. The verify request then goes out with an empty bearer, the
      // backend answers {error: "Token not available"}, and this treated it as an
      // expired session -- bouncing a freshly signed-in user back to /login, which
      // re-initialises Google Sign-In and redirects here again. A loop.
      //
      // Don't send the request without a token at all; give the store a tick to
      // settle first so "still signing in" is distinguishable from "guest".
      if (!token) {
        this.$nextTick(() => {
          if (!this.$store.getters.getGToken) {
            this.$router.push({ name: 'Login', query: { redirect: this.$route.fullPath } })
          } else {
            this.fetch()
          }
        })
        return
      }

      // First verify the token is still valid
      axios.post('/mywishboard/api/wishboard/user/token/verify/', {},
        { headers: { Authorization: 'Bearer ' + token } }
      )
      .then(verifyRes => {
        // Backend returns 200 with {error: ...} on expiry — treat that as session expired
        if (verifyRes.data && verifyRes.data.error) {
          this.$store.commit('setGToken', '')
          this.$router.push({ name: 'Login', query: { redirect: this.$route.fullPath } })
          return
        }

        // Token OK — fetch boards
        return axios.post('/mywishboard/api/wishboard/list/',
          { user },
          { headers: { Authorization: 'Bearer ' + token } }
        )
      })
      .then(response => {
        if (!response) return // redirected above
        this.wishboards = response.data
        this.loading = false
      })
      .catch(err => {
        this.loading = false
        const status = err.response && err.response.status
        if (status === 401 || status === 403) {
          this.$store.commit('setGToken', '')
          this.$router.push({ name: 'Login', query: { redirect: this.$route.fullPath } })
        } else {
          this.fetchError = 'Could not load your boards. Please check your connection and try again.'
        }
      })
    }
  },
  mounted() {
    this.emitter.emit('showlogout', 'true')
    this.emitter.emit('showWishboardCreate', 'true')
    this.fetch()
  }
}
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; }

.boards-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  min-height: 100vh;
  background: #f7f7f7;
  padding: 40px 32px 80px;
}

.boards-inner {
  max-width: 1100px;
  margin: 0 auto;
}

/* ── HEADER ── */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
}

.page-header h1 {
  font-size: 1.6rem;
  font-weight: 800;
  letter-spacing: -0.4px;
  color: #0a0a0a;
  margin: 0 0 4px;
}

.page-sub {
  font-size: 0.78rem;
  color: #bbb;
  margin: 0;
}

.btn-new {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: #0a0a0a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.18s, transform 0.15s;
  white-space: nowrap;
}

.btn-new:hover { background: #2a2a2a; transform: translateY(-1px); }

/* ── STATES ── */
.state-center {
  text-align: center;
  padding: 96px 24px;
  color: #bbb;
}

.state-icon {
  font-size: 2.4rem;
  display: block;
  margin-bottom: 16px;
  color: #ddd;
}

.state-icon.warn { color: #f0a0a0; }
.state-center p { font-size: 0.9rem; color: #aaa; margin-bottom: 24px; }

.btn-ghost {
  padding: 9px 20px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  color: #333;
}

/* ── HERO CARD ── */
.hero-card {
  width: 100%;
  height: 340px;
  border-radius: 18px;
  background: #1a1a1a;
  background-size: cover;
  background-position: center;
  cursor: pointer;
  overflow: hidden;
  margin-bottom: 32px;
  position: relative;
  transition: transform 0.2s, box-shadow 0.2s;
}

.hero-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 48px rgba(0,0,0,0.2);
}

.hero-card-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 50%, transparent 100%);
  display: flex;
  align-items: flex-end;
}

.hero-card-inner {
  padding: 28px 32px;
  width: 100%;
}

.hero-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.hero-badge {
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  background: #e8a020;
  color: #fff;
  padding: 3px 8px;
  border-radius: 4px;
}

.hero-date {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.6);
}

.hero-title {
  font-size: 1.8rem;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.4px;
  margin: 0 0 6px;
  line-height: 1.15;
}

.hero-sub {
  font-size: 0.88rem;
  color: rgba(255,255,255,0.65);
  margin: 0 0 20px;
}

.hero-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.hero-wishes {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.7);
}

.hero-wishes i { font-size: 1rem; }

.hero-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 22px;
  background: #fff;
  color: #0a0a0a;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
}

.hero-btn:hover { background: #f0f0f0; }

/* ── CONTROLS ── */
.controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
}

.controls-left {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.filter-pill {
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid #e8e8e8;
  background: #fff;
  font-size: 0.78rem;
  font-weight: 600;
  color: #888;
  cursor: pointer;
  transition: all 0.15s;
}

.filter-pill:hover { border-color: #ccc; color: #333; }
.filter-pill.active {
  background: #0a0a0a;
  border-color: #0a0a0a;
  color: #fff;
}

.controls-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sort-select {
  font-size: 0.78rem;
  font-weight: 600;
  color: #555;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 6px 10px;
  background: #fff;
  cursor: pointer;
  outline: none;
  font-family: inherit;
  transition: border-color 0.15s;
}

.sort-select:focus { border-color: #e8a020; }

.layout-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
  background: #fff;
  color: #aaa;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
}

.layout-btn:hover { border-color: #ccc; color: #333; }
.layout-btn.active { background: #0a0a0a; border-color: #0a0a0a; color: #fff; }

/* ── LIST VIEW ── */
.list-view {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.list-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  background: #fff;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.15s, transform 0.15s;
}

.list-item:hover { background: #f9f9f9; transform: translateX(2px); }

.list-thumb {
  width: 52px;
  height: 52px;
  border-radius: 10px;
  background: #f0f0f0;
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.list-thumb-icon { font-size: 1.3rem; color: #ddd; }

.list-info { flex: 1; min-width: 0; }

.list-title {
  font-size: 0.92rem;
  font-weight: 700;
  color: #0a0a0a;
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.list-meta {
  font-size: 0.73rem;
  color: #bbb;
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}

.badge-premium-inline {
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  background: #fdf3e0;
  color: #b07010;
  padding: 2px 6px;
  border-radius: 3px;
}

.list-progress { margin-top: 2px; }

.list-progress-bar {
  width: 100px;
  height: 3px;
  background: #f0f0f0;
  border-radius: 2px;
  overflow: hidden;
}

.list-progress-fill {
  height: 100%;
  background: #e8a020;
  border-radius: 2px;
}

.list-progress-fill.warn { background: #e84040; }

.list-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.list-open-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid #ebebeb;
  background: #fff;
  color: #555;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
}

.list-open-btn:hover { background: #0a0a0a; color: #fff; border-color: #0a0a0a; }
.rest-section { }

.rest-heading {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #bbb;
  margin-bottom: 16px;
}

/* 2-col card grid */
.rest-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.rest-card {
  height: 200px;
  border-radius: 14px;
  background: #1a1a1a;
  background-size: cover;
  background-position: center;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  transition: transform 0.2s, box-shadow 0.2s;
}

.rest-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 32px rgba(0,0,0,0.18);
}

.rest-card-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.2) 55%, transparent 100%);
  display: flex;
  align-items: flex-end;
}

.rest-card-inner {
  padding: 14px 16px;
  width: 100%;
}

.rest-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.rest-card-date {
  font-size: 0.65rem;
  color: rgba(255,255,255,0.45);
  margin-left: auto;
}

.badge-premium {
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  background: #e8a020;
  color: #fff;
  padding: 2px 7px;
  border-radius: 4px;
}

.rest-card-body {
  margin-bottom: 10px;
}

.rest-card-title {
  font-size: 0.92rem;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.2px;
  line-height: 1.2;
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rest-card-for {
  font-size: 0.72rem;
  color: rgba(255,255,255,0.55);
}

.rest-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.rest-card-wishes {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  color: rgba(255,255,255,0.55);
}

.rest-card-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-upgrade {
  padding: 5px 10px;
  background: rgba(232,160,32,0.9);
  color: #fff;
  border: none;
  border-radius: 5px;
  font-size: 0.68rem;
  font-weight: 700;
  cursor: pointer;
}

.rest-open-btn {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 5px 10px;
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(4px);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 5px;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
}

.rest-open-btn:hover {
  background: rgba(255,255,255,0.25);
}

/* Progress */
.rest-progress { }

.rest-progress-bar {
  height: 2px;
  background: rgba(255,255,255,0.15);
  border-radius: 2px;
  overflow: hidden;
}

.rest-progress-fill {
  height: 100%;
  background: #e8a020;
  border-radius: 2px;
  transition: width 0.4s;
}

.rest-progress-fill.warn { background: #e84040; }

/* ── RESPONSIVE ── */
@media (max-width: 600px) {
  .boards-page { padding: 24px 16px 60px; }
  .page-header h1 { font-size: 1.3rem; }
  .hero-card { height: 240px; }
  .hero-title { font-size: 1.3rem; }
  .hero-card-inner { padding: 18px; }
  .rest-grid { grid-template-columns: 1fr; }
  .rest-card { height: 170px; }
  .controls { flex-direction: column; align-items: flex-start; }
  .controls-right { width: 100%; justify-content: flex-end; }
  .list-item { padding: 12px; gap: 12px; }
}
</style>
